import { Combobox as BaseCombobox, ComboboxItemState } from '@base-ui-components/react/combobox';
import clsx from 'clsx';
import { ForwardedRef, forwardRef, HTMLProps, KeyboardEvent, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mergeRefs } from 'react-merge-refs';
import { refHandler } from '../../utils';
import { createTestIdFactory } from '../../utils/create-testid-factory/create-testid-factory';
import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import { InputWrapper, InputWrapperProps } from '../input-wrapper';
import { OptionGroup } from '../option-group';
import { OptionItem } from '../option-item';
import { Typography } from '../typography/index.ts';
import classes from './combobox.module.scss';
import { ComboboxOption } from './types.ts';

export type ComboboxProps<T extends string | number = string, D = unknown> = {
  /**
   * Represents a list of combobox options.
   * Each item in the array corresponds to an option available in the combobox.
   * Typically used to populate the options of a dropdown menu or similar UI component.
   */
  items: ComboboxOption<T, D>[];

  /**
   * An optional placeholder text that provides a hint or descriptive text
   * to be displayed inside an input field or similar UI element when it is empty.
   * It helps users understand what kind of input is expected.
   */
  placeholder?: string;

  /**
   * An optional string value that can be used to store or represent textual information.
   * If not provided, the value will be undefined.
   */
  value?: T;

  /**
   * Whether to allow the clearing of the value
   */
  clearable?: boolean;

  /**
   * Event triggered, if enter key is hit and now search results are available
   */
  onCreate?: (query: string) => ComboboxOption<T, D> | void;

  /**
   * Callback function triggered when the selected value changes.
   */
  onChange?: (value: ComboboxOption<T, D> | null) => void;

  /**
   * A callback function for rendering a custom option in a combobox component.
   */
  renderOption?: (data: {
    option: ComboboxOption<T, D>;
    state: ComboboxItemState;
    props: Omit<HTMLProps<unknown>, 'prefix' | 'color' | 'as'>;
  }) => ReactElement;

} & Omit<InputWrapperProps<T>, 'ref' | 'onChange' | 'value' | 'multiple'>
  & Omit<HTMLProps<HTMLDivElement>, 'ref' | 'label' | 'name' | 'disabled' | 'value' | 'prefix' | 'suffix' | 'onChange' | 'multiple'>;

/**
 * A configurable Combobox component for selecting and searching items from a provided list.
 *
 * @remarks
 * This component allows users to search and select an item from a list. It supports features like a clear button to reset the selection, dynamic filtering of items, and an accessible UI compliant with keyboard navigation patterns. Internally, the component uses a sophisticated rendering strategy for performance optimization and modular composition.
 */
const ComboboxInner = <T extends string | number = string, D = unknown>({
  items,
  value,
  onChange,
  placeholder,
  disabled,
  clearable = true,
  onCreate,
  renderOption,
  ...props
}: ComboboxProps<T, D>, ref: ForwardedRef<HTMLInputElement>) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const valueItem = useMemo(() => {
    return items.find(item => item.value === value);
  }, [value, items]);
  const testId = createTestIdFactory(props['data-testid']);

  const [searchQuery, setSearchQuery] = useState(valueItem?.label ?? '');

  const onClear = () => {
    setSearchQuery('');
  };

  const onEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!onCreate) {
      return;
    }
    const newItem = onCreate?.((e.target as HTMLInputElement).value);
    if (newItem) {
      onChange?.(newItem);
    }
    resetInputText();
  };

  const resetInputText = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (!valueItem) {
      setSearchQuery('');
      resetInputText();
      return;
    }
    setSearchQuery(valueItem?.label);
  }, [valueItem]);

  return (
    <BaseCombobox.Root<ComboboxOption<T> | null>
      disabled={disabled}
      items={items}
      value={valueItem ?? null}
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      /* @ts-expect-error */
      onValueChange={onChange}
    >
      <InputWrapper
        {...props}
        disabled={disabled}
        rootRef={refHandler(containerRef)}
        onEnterKey={onEnter}
        suffix={(
          <>
            {clearable && <BaseCombobox.Clear
              onClick={onClear}
              render={(props, { disabled }) => <IconButton
                {...props}
                disabled={disabled}
                label={t('common.remove')}
                data-testid={testId('clear')}
                color="grey"
                variant={'text'}>
                <Icon icon={['far', 'times']} />
              </IconButton>
              } />}
            <BaseCombobox.Trigger render={(props, state) => {
              const { color, ...rest } = props;
              void color;

              return (
                <IconButton
                  data-testid={testId('toggle-popup')}
                  label={t('common.open')}
                  color="grey"
                  variant={'text'}
                  {...rest}
                >
                  <Icon icon={['far', state.open ? 'chevron-up' : 'chevron-down']} />
                </IconButton>
              );
            }} />
          </>
        )}>
        {({ id, input, accessibility, listeners }) => (
          <BaseCombobox.Input
            id={id}
            {...input}
            ref={mergeRefs([input.ref, ref, inputRef])}
            {...listeners}
            {...accessibility}
            data-testid={testId('combobox-input')}
            className={clsx(input.className, classes.input)}
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            placeholder={placeholder} />
        )}
      </InputWrapper>

      <BaseCombobox.Portal>
        <BaseCombobox.Positioner sideOffset={4} className={classes.positioner} anchor={containerRef}>
          <BaseCombobox.Popup className={classes.popup}>
            {onCreate ? <BaseCombobox.Empty><Typography variant='caption' dangerouslySetInnerHTML={{ __html: t('common.no-entries-but-creatable') }} /></BaseCombobox.Empty> : <BaseCombobox.Empty><Typography variant="caption" dangerouslySetInnerHTML={{ __html: t('common.no-entries') }} /></BaseCombobox.Empty>}
            <BaseCombobox.List render={<OptionGroup />}>
              {(item: ComboboxOption<T, D>) => (
                <BaseCombobox.Item
                  key={item.value}
                  value={item}
                  disabled={item.disabled}
                  render={(props, state) => {
                    const { prefix, color, ...rest } = props;
                    void prefix;
                    void color;

                    if (renderOption) {
                      return renderOption({
                        option: item,
                        state,
                        props: rest,
                      });
                    }


                    return (
                      <OptionItem
                        data-testid={testId(item.value.toString())}
                        isFocused={state.highlighted}
                        selected={state.selected}
                        disabled={state.disabled}
                        {...rest}
                      />
                    );
                  }}>
                  {/*<BaseCombobox.ItemIndicator><Icon icon={['far', 'check']} /></BaseCombobox.ItemIndicator>*/}
                  <div>{item.label}</div>
                </BaseCombobox.Item>
              )}
            </BaseCombobox.List>
          </BaseCombobox.Popup>
        </BaseCombobox.Positioner>
      </BaseCombobox.Portal>
    </BaseCombobox.Root>
  );
};

export const Combobox = forwardRef(ComboboxInner) as <T extends number | string = string, D = unknown>(
  props: ComboboxProps<T, D> & { ref?: ForwardedRef<HTMLDivElement> }
) => ReturnType<typeof ComboboxInner>;